import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Skeleton, Typography } from '@mui/material';
import { Check, GroupOutlined } from '@mui/icons-material';
import { TaskActions } from '../actions/useTaskActions';
import { clientMeta, formatClock, ScheduledSession } from '../schedule';
import { clientHref } from '../tasks/links';
import { INK, visuallyHidden } from '../ui';
import { STATUS_CHIPS } from './scheduleSource';
import { TodayTasks } from './TodayTasks';

const RAIL = { display: 'grid', gridTemplateColumns: { xs: '64px 18px minmax(0, 1fr)', sm: '72px 20px minmax(0, 1fr)' } } as const;

/** Sits at the top of the next session's list item, so screen readers count sessions, not markers. */
const NowLine: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ ...RAIL, alignItems: 'center', mb: 1.5 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
      <Box component="span" sx={visuallyHidden}>Now, </Box>
      {label}
    </Typography>
    <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', justifySelf: 'center' }} />
    <Box aria-hidden sx={{ height: 2, bgcolor: 'primary.main', borderRadius: 1 }} />
  </Box>
);

/** The timeline dot of a session with nothing outstanding, so clear sessions read down one column. It stays green once the session has passed. */
const ClearNode: React.FC = () => (
  <Box
    role="img"
    aria-label="Nothing outstanding"
    sx={{ mt: 0.25, width: 16, height: 16, borderRadius: '50%', bgcolor: '#15803D', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Check aria-hidden sx={{ fontSize: 12, stroke: 'currentColor', strokeWidth: 1.5 }} />
  </Box>
);

const pillSx = { fontSize: 12, fontWeight: 500, color: INK.body, bgcolor: '#EEF1F5', px: 1, py: '2px', borderRadius: 999 } as const;

/**
 * The day's sessions in time order. Each client's open tasks sit under the
 * session where the coach will see them, grouped by what to do; past sessions
 * fade but keep theirs (a check-in is usually logged right after).
 * Sessions for another coach's client name that coach and carry no tasks.
 * From the Mindbody schedule, a card also carries the session type, a status
 * chip, and late cancels and no-shows read muted; a client the list doesn't
 * have yet gets a schedule-only card with no link and no tasks.
 */
export const TodaySchedule: React.FC<{
  sessions: ScheduledSession[];
  actions: TaskActions;
  now: Date;
  showNow: boolean;
  checking: boolean;
  /** Scheduled clients are still being looked up, so a card without a record doesn't yet say the client is missing. */
  resolvingClients?: boolean;
  /** Clients whose tasks were worked out; anyone else (looked up only for the schedule) gets no tick. All, when absent. */
  taskClientIds?: ReadonlySet<string>;
}> = ({ sessions, actions, now, showNow, checking, resolvingClients = false, taskClientIds }) => {
  // The Now line marks the next booked session; a late cancel or no-show never anchors it.
  const nowIndex = showNow ? sessions.findIndex((s) => s.countable && !s.past) : -1;
  const nowLabel = formatClock({ hours: now.getHours(), minutes: now.getMinutes() });
  const soonest = nowIndex >= 0 ? sessions[nowIndex] : null;
  const minutesAway = soonest?.appointment.time
    ? soonest.appointment.time.hours * 60 + soonest.appointment.time.minutes - (now.getHours() * 60 + now.getMinutes())
    : null;

  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {sessions.map((session, index) => {
        const { client, appointment, work, past, collaboration, countable } = session;
        const tasks = work?.tasks ?? [];
        // A schedule-only card has no tasks to check; a collaboration's are the lead coach's; a late cancel isn't a session to tick off.
        const tasksKnown = client !== null && !collaboration && (!taskClientIds || taskClientIds.has(session.clientId));
        const clear = tasksKnown && countable && tasks.length === 0 && !checking;
        const faded = past || !countable;
        const chip = session.status ? STATUS_CHIPS[session.status] : undefined;
        const details = [session.durationMin ? `${session.durationMin} min` : null, session.sessionTypeName].filter(Boolean).join(' · ');
        return (
          <Box component="li" key={session.key}>
            {index === nowIndex ? <NowLine label={nowLabel} /> : null}
            <Box sx={RAIL}>
              <Box sx={{ pt: 0.25 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: faded ? INK.muted : INK.main }}>{formatClock(appointment.time)}</Typography>
                <Typography sx={{ fontSize: 12, color: INK.muted }}>{appointment.location}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {clear ? (
                  <ClearNode />
                ) : (
                  <Box
                    aria-hidden
                    sx={{
                      mt: 0.75,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      boxSizing: 'border-box',
                      border: '2px solid',
                      borderColor: index === nowIndex ? 'primary.main' : '#94A3B8',
                      bgcolor: past ? '#94A3B8' : INK.ground,
                    }}
                  />
                )}
                <Box aria-hidden sx={{ flexGrow: 1, width: 2, bgcolor: INK.hairline, mt: 0.5 }} />
              </Box>
              <Box sx={{ pl: 1, pb: 3.5, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.25 }}>
                  {client ? (
                    <Link
                      component={RouterLink}
                      to={clientHref(client)}
                      underline="hover"
                      sx={{ fontSize: 15, fontWeight: 600, color: !countable ? INK.muted : past ? INK.body : INK.main }}
                    >
                      {session.clientName}
                    </Link>
                  ) : (
                    <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: !countable ? INK.muted : past ? INK.body : INK.main }}>
                      {session.clientName}
                    </Typography>
                  )}
                  {client ? (
                    <Typography component="span" sx={{ fontSize: 13, color: INK.muted }}>
                      {clientMeta(client)}
                    </Typography>
                  ) : null}
                  {chip ? (
                    <Box component="span" sx={{ ...pillSx, alignSelf: 'center' }}>
                      {chip}
                    </Box>
                  ) : null}
                  {collaboration && client ? (
                    <Box component="span" sx={{ ...pillSx, display: 'inline-flex', alignItems: 'center', gap: 0.75, alignSelf: 'center' }}>
                      <GroupOutlined aria-hidden sx={{ fontSize: 14 }} />
                      {client.coachId ? `Lead coach ${client.coachName}` : 'No lead coach'}
                    </Box>
                  ) : null}
                  {index === nowIndex && minutesAway !== null && minutesAway >= 0 && minutesAway <= 60 ? (
                    <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'primary.dark', bgcolor: '#EEF3FE', px: 1, borderRadius: 999 }}>
                      {minutesAway === 0 ? 'Now' : `In ${minutesAway} min`}
                    </Box>
                  ) : null}
                </Box>
                {details ? <Typography sx={{ fontSize: 13, color: INK.muted, mt: 0.25 }}>{details}</Typography> : null}
                {!client && !resolvingClients ? <Typography sx={{ fontSize: 13, color: INK.muted, mt: 0.25 }}>Not in the client list yet</Typography> : null}
                {client && tasks.length > 0 ? (
                  <TodayTasks client={client} tasks={tasks} actions={actions} now={now} />
                ) : tasksKnown && checking ? (
                  <Box aria-label="Checking what is due" sx={{ mt: 1 }}>
                    <Skeleton width="46%" height={16} />
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
